/**
 * D282, the console's half of "watch and control a live workbench session": what the Live page draws,
 * as plain data, and how each event of `sessions.subscribe` changes it. No React here, so the hook
 * (useLiveSession.ts) and the pages share one reading of the stream. The shapes are the workbench's
 * (`@aioe/shared` remoteControl.ts, mirrored from mju-ai-workbench/shared/schemas/remoteControl.ts).
 */

import { backendSaid, foldTranscript, LIVE_STATUS_WORDS, riskLevelSentence, transcriptFromSnapshot, type LiveApproval, type LiveEvent, type LiveSnapshot, type LiveStatus, type LiveTask, type LiveTranscriptItem, type LiveUsage, type RemoteSession, type RemoteState } from '@aioe/shared';
import { dateMedium, time } from '@aioe/shared/wording/dateTime';

/** Which session a live view is of: a conversation (Chat, Research, Team Work) or a bare agent session. */
export type LiveTarget = { conversationId: string; sessionId?: undefined } | { sessionId: string; conversationId?: undefined };

/** The address's `:key`: `c:<conversationId>` or `s:<sessionId>`. */
export function parseLiveKey(key: string | undefined): LiveTarget | undefined {
  if (!key) return undefined;
  if (key.startsWith('c:') && key.length > 2) return { conversationId: key.slice(2) };
  if (key.startsWith('s:') && key.length > 2) return { sessionId: key.slice(2) };
  return undefined;
}

export function liveKey(s: { conversationId?: string; sessionId?: string }): string | undefined {
  return s.conversationId ? `c:${s.conversationId}` : s.sessionId ? `s:${s.sessionId}` : undefined;
}

/** The Live page's address for a session on a machine; `project` lets a watcher's page find the row it came from. */
export function liveHref(deviceId: string, s: { conversationId?: string; sessionId?: string }, project?: string): string | undefined {
  const key = liveKey(s);
  if (!key) return undefined;
  return `/live/${encodeURIComponent(deviceId)}/${encodeURIComponent(key)}${project ? `?project=${encodeURIComponent(project)}` : ''}`;
}

export const MODE_WORDS: Record<RemoteSession['mode'], string> = { chat: 'Chat', research: 'Research', teamwork: 'Team Work', agent: 'Agent session' };

/** A status as a StatusMark: the dot's state and the workbench's own words. */
export function statusMark(status: LiveStatus | undefined, words?: string): { state: 'working' | 'medium' | 'connected' | 'idle' | 'online' | 'disabled'; label: string } {
  if (!status) return { state: 'disabled', label: words ?? 'Not known yet' };
  const state = status === 'thinking' ? 'working' : status === 'waiting' ? 'medium' : status === 'done' ? 'connected' : status === 'stopped' ? 'idle' : 'online';
  return { state, label: words || LIVE_STATUS_WORDS[status] };
}

const TOOL_STATE_WORDS: Record<string, string> = { pending: 'waiting', in_progress: 'running', completed: 'done', failed: 'failed' };

/** One quiet line for a tool call: what it was, its D270 level in words, where it stands. */
export function toolLine(t: { title?: string; toolKind?: string; state?: string; riskLevel?: number }): string {
  const what = t.title?.trim() || (t.toolKind ? `A ${t.toolKind.replace(/[_-]+/g, ' ')} tool` : 'A tool');
  return [what, t.riskLevel ? riskLevelSentence(t.riskLevel) : undefined, t.state ? (TOOL_STATE_WORDS[t.state] ?? t.state.replace(/_/g, ' ')) : undefined].filter(Boolean).join(' · ');
}

/** "Risk level 3 out of 5: needs a look" for an approval, preferring the workbench's own sentence. */
export const approvalRisk = (a: Pick<LiveApproval, 'risk' | 'riskLevel'>) => a.risk?.trim() || riskLevelSentence(a.riskLevel);

/** "just now", "1 minute ago", "10 minutes ago": a last-seen time in words. */
export function agoWords(iso: string | undefined, now = Date.now()): string | undefined {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return undefined;
  const s = Math.max(0, (now - t) / 1000);
  const n = (v: number, one: string) => `${v} ${one}${v === 1 ? '' : 's'} ago`;
  if (s < 45) return 'just now';
  if (s < 3600) return n(Math.max(1, Math.round(s / 60)), 'minute');
  if (s < 86_400) return n(Math.round(s / 3600), 'hour');
  return n(Math.round(s / 86_400), 'day');
}

/** "10:02" today, "17 Oct, 10:02" before. */
export function clockWords(iso: string | undefined, now = Date.now()): string | undefined {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return undefined;
  const d = new Date(t);
  const at = time(d);
  return new Date(now).toDateString() === d.toDateString() ? at : `${dateMedium(d, { year: false })}, ${at}`;
}

/** One line of the transcript: a message, a tool call or a turn's changes (`@aioe/shared` remote/liveTranscript.ts). */
export type LiveItem = LiveTranscriptItem;

/** A task of the run as the strip shows it, with the board it is on when the stream named it. */
export type LiveProgress = LiveTask & { teamId?: string };

/** The usage limit the agent waits for (D372), as the banner shows it. */
export interface LiveLimit {
  provider: string;
  account?: string;
  resetsAt?: string;
  handOff?: boolean;
  at: string;
  /** The agent session at its limit (the event's), for "Hand off to…" (`sessions.handOff`). */
  sessionId?: string;
  /**
   * What the backend said, in its own words: the limit event's `detail` (D414, masked by the workbench), else the
   * stopped status's sentence that came with the limit (a workbench before D414).
   */
  said?: string;
  /** The said words came on the event itself (D414): a later status does not replace them. */
  saidByEvent?: boolean;
  /** The model the session ran (D414). */
  model?: string;
}

/** Everything the Live page draws from the stream. */
export interface LiveState {
  items: LiveItem[];
  /** The stream names its messages as turns (a workbench with D372). */
  turns: boolean;
  approvals: LiveApproval[];
  status?: LiveStatus;
  words?: string;
  /** The status event's own sentence, when it carried one. */
  message?: string;
  usage?: LiveUsage;
  progress: LiveProgress[];
  /** The board the run's tasks are on, once a `progress` event named it (a snapshot's tasks do not). */
  teamId?: string;
  /** The board was paused or resumed from here (the stream does not say which it is). */
  paused?: boolean;
  /** The agent is waiting for its usage limit to lift. */
  limit?: LiveLimit;
  remote?: RemoteState;
  /** The highest `seq` held: an event at or below it is dropped. */
  seq: number;
  /** A snapshot has been drawn (the first answer always carries one). */
  ready: boolean;
}

export const EMPTY_LIVE: LiveState = { items: [], turns: false, approvals: [], progress: [], seq: -1, ready: false };

/**
 * A snapshot replaces what was drawn; the stream goes on from it. What a snapshot does not carry (the board) stays. Its
 * `paused` (workbench 0.89.22) says whether the board is paused; an older workbench never sends it, and then a pause
 * asked from here stands.
 */
export function fromSnapshot(snap: LiveSnapshot, remote: RemoteState | undefined, sessionId: string | undefined, held?: Pick<LiveState, 'teamId' | 'paused'>): LiveState {
  const t = transcriptFromSnapshot(snap.turns);
  return {
    items: t.items,
    turns: t.turns,
    approvals: (snap.approvals ?? []).map((a) => ({ ...a, sessionId: a.sessionId || sessionId || '' })),
    status: snap.status,
    words: snap.words,
    usage: snap.usage,
    progress: snap.progress ?? [],
    ...(held?.teamId ? { teamId: held.teamId } : {}),
    ...(snap.paused ? { paused: true } : held?.paused !== undefined ? { paused: held.paused } : {}),
    remote,
    seq: -1,
    ready: true,
  };
}

const defined = <T extends object>(o: T): Partial<T> => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;

/** One event applied in order. An event the state already holds (by `seq`) changes nothing. */
export function applyEvent(s: LiveState, e: LiveEvent, sessionId?: string): LiveState {
  if (e.seq <= s.seq) return s;
  const next: LiveState = { ...s, seq: e.seq };
  const key = `e-${e.seq}`;
  switch (e.kind) {
    case 'message':
    case 'delta':
    case 'turn':
    case 'tool':
    case 'changes': {
      const t = foldTranscript({ items: s.items, turns: s.turns }, e, key);
      next.items = t.items;
      next.turns = t.turns;
      // The agent is answering again: whatever limit it waited for has lifted.
      if (e.kind === 'delta' || (e.kind === 'turn' && e.role === 'assistant')) next.limit = undefined;
      break;
    }
    case 'approval': {
      const { seq: _seq, at: _at, kind: _kind, conversationId: _c, sessionId: sid, ...rest } = e;
      const a: LiveApproval = { ...rest, sessionId: sid || sessionId || '' };
      next.approvals = [...s.approvals.filter((x) => x.permissionId !== a.permissionId), a];
      break;
    }
    case 'board':
      // Paused or resumed from anywhere: the workbench, the console or the phone (0.89.22).
      next.paused = e.paused;
      if (!s.teamId) next.teamId = e.teamId;
      break;
    case 'approval.answered':
      next.approvals = s.approvals.filter((x) => x.permissionId !== e.permissionId);
      break;
    case 'progress': {
      const { seq: _seq, at: _at, kind: _kind, conversationId: _c, sessionId: _s, ...task } = e;
      const p: LiveProgress = task;
      const i = s.progress.findIndex((x) => x.taskId === e.taskId);
      next.progress = i >= 0 ? s.progress.map((x, j) => (j === i ? { ...x, ...defined(p) } : x)) : [...s.progress, p];
      if (e.teamId) next.teamId = e.teamId;
      break;
    }
    case 'usage':
      next.usage = { ...s.usage, ...(e.contextUsed !== undefined ? { contextUsed: e.contextUsed } : {}), ...(e.contextSize !== undefined ? { contextSize: e.contextSize } : {}), ...(e.costUsd !== undefined ? { costUsd: e.costUsd } : {}) };
      break;
    case 'status':
      next.status = e.status;
      next.words = e.words;
      next.message = e.message;
      if (e.status === 'thinking') next.limit = undefined;
      // The provider's own words for a limit already shown: kept on the banner for "What the backend said".
      else if (s.limit && !s.limit.saidByEvent && backendSaid(e.message)) next.limit = { ...s.limit, said: backendSaid(e.message) };
      break;
    case 'remote':
      next.remote = { ...s.remote, on: e.on, ...(e.controlledBy ? { controlledBy: { ...e.controlledBy, at: e.at } } : { controlledBy: undefined }) };
      break;
    case 'limit':
      next.limit = {
        provider: e.provider,
        at: e.at,
        ...(e.account ? { account: e.account } : {}),
        ...(e.resetsAt ? { resetsAt: e.resetsAt } : {}),
        ...(e.handOff ? { handOff: true } : {}),
        ...((e.sessionId ?? sessionId) ? { sessionId: e.sessionId ?? sessionId } : {}),
        ...(e.model?.trim() ? { model: e.model.trim() } : {}),
        // D414: the backend's words on the event; before it, the stopped status just before carried them.
        ...(backendSaid(e.detail) ? { said: backendSaid(e.detail), saidByEvent: true } : s.status === 'stopped' && backendSaid(s.message) ? { said: backendSaid(s.message) } : {}),
      };
      break;
  }
  return next;
}

/** Where a personal notification's live session runs (D282): set on an approval waiting on a remotely controlled session. */
export interface NotificationLive {
  deviceId: string;
  conversationId?: string;
  sessionId?: string;
}

/**
 * The live fields a personal notification may carry, read loosely so a record from before D282 (or a
 * shared `Notification` type that does not name them yet) reads as none: `live`, and for an approval the
 * `permissionId`, `sessionId` and `ownerRequired` that `permissions.resolve` needs.
 */
export function liveOfNotification(n: object): { live: NotificationLive; permissionId?: string; sessionId?: string; ownerRequired?: boolean } | undefined {
  const r = n as { live?: unknown; permissionId?: unknown; sessionId?: unknown; ownerRequired?: unknown };
  const l = r.live as { deviceId?: unknown; conversationId?: unknown; sessionId?: unknown } | undefined;
  if (!l || typeof l !== 'object' || typeof l.deviceId !== 'string' || !l.deviceId) return undefined;
  const s = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  return {
    live: { deviceId: l.deviceId, ...(s(l.conversationId) ? { conversationId: s(l.conversationId) } : {}), ...(s(l.sessionId) ? { sessionId: s(l.sessionId) } : {}) },
    ...(s(r.permissionId) ? { permissionId: s(r.permissionId) } : {}),
    ...(s(r.sessionId) ?? s(l.sessionId) ? { sessionId: s(r.sessionId) ?? s(l.sessionId) } : {}),
    ...(r.ownerRequired === true ? { ownerRequired: true } : {}),
  };
}

/** An inbox action that answers a live approval: the workbench's own method, or an approve / refuse by its id. */
export const answersApproval = (a: { id: string; rpc: string }) => a.rpc === 'permissions.resolve' || /^(approve|allow|refuse|deny|reject)/i.test(a.id);
/** What an answering action decides. */
export const approvalDecision = (a: { id: string; params?: Record<string, unknown> }): 'allow' | 'deny' =>
  a.params?.decision === 'deny' || a.params?.decision === 'allow' ? (a.params.decision as 'allow' | 'deny') : /^(refuse|deny|reject)/i.test(a.id) ? 'deny' : 'allow';
